/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ORDER_FULFILLMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { UpdateFulfillmentDetailsPayload } from 'common/types/redux/orders';
import { Comment, Note, DocumentContact } from 'common/types';
import { FolderAttachment, Order } from 'common/types/orders';
import { UpdateKeyValuePairPayload } from 'common/stores/redux/interfaces';

interface State {
    order: Order | null;
    initialized: boolean;
    filesInitialized: boolean;
    historyInitialized: boolean;
    finances: unknown;
}

const initialState: State = {
    order: null,
    initialized: false,
    filesInitialized: false,
    historyInitialized: false,
    finances: null,
};

const adminOrderSlice = createSlice({
    name: 'adminOrder',
    initialState,
    reducers: {
        initialize: (state: State, action: PayloadAction<Order>) => {
            state.initialized = true;
            state.order = action.payload;
        },

        initializeRoute: (state: State, action: PayloadAction<string>) => {
            const key = `${action.payload}Initialized`;

            if (key === 'filesInitialized' || key === 'historyInitialized') {
                state[key] = true;
            }
        },

        initializeFinancialInformation: (state: State, action) => {
            state.finances = action.payload;
        },

        setOrder: (state: State, action: PayloadAction<Order>) => {
            state.order = action.payload;
        },

        updateOrder: (state: State, action: PayloadAction<Order>) => {
            state.order = {
                ...state.order,
                ...action.payload,
            };
        },

        setFinanceEdit: (state: State, action: PayloadAction<boolean>) => {
            if (!state.order) {
                return;
            }

            const financeEdit = action.payload;

            state.order = {
                ...state.order,
                financeEdit,
                originalLineItems: financeEdit
                    ? state.order.lineItems
                    : undefined,
                lineItems: financeEdit
                    ? state.order.lineItems
                    : state.order.originalLineItems,
            };
        },

        resetInitialization: (state: State) => {
            state.initialized = false;
            state.filesInitialized = false;
            state.historyInitialized = false;
        },

        updateKeyValuePair: (
            state: State,
            action: PayloadAction<UpdateKeyValuePairPayload>
        ) => {
            if (!state.order) return;

            const { key, value } = action.payload;
            const update: Record<string, unknown> = {};

            if (Array.isArray(key)) {
                for (let idx = 0; idx < key.length; idx++) {
                    update[key[idx]] = value[idx];
                }
            } else {
                update[key] = value;
            }

            state.order = { ...state.order, ...update };
        },

        updateSpecialInstructions: (
            state: State,
            action: PayloadAction<string>
        ) => {
            if (!state.order) {
                return;
            }

            state.order = {
                ...state.order,
                instructions: {
                    special: action.payload,
                },
            };
        },

        addNote: (state: State, action: PayloadAction<Note>) => {
            if (!state.order) {
                return;
            }

            const currentNotes = [...(state.order.job.notes ?? [])];
            const noteToAdd = action.payload;

            const updatedNotes = [...currentNotes, noteToAdd];

            state.order = {
                ...state.order,
                job: {
                    ...state.order.job,
                    notes: updatedNotes,
                },
            };
        },

        addContact: (state: State, action: PayloadAction<DocumentContact>) => {
            if (!state.order) {
                return;
            }

            state.order.contacts = [
                ...(state.order.contacts ?? []),
                action.payload,
            ];
        },

        removeContact: (state: State, action: PayloadAction<number>) => {
            if (!state.order) {
                return;
            }
            const _contacts = state.order.contacts?.filter(
                contact => contact.id !== action.payload
            );
            state.order = {
                ...state.order,
                contacts: _contacts,
            };
        },

        addComment: (state: State, action: PayloadAction<Comment>) => {
            if (!state.order) {
                return;
            }

            const currentComments = [...(state.order.comments ?? [])];
            const commentToAdd = action.payload;

            state.order = {
                ...state.order,
                comments: [...currentComments, commentToAdd],
            };
        },

        updateComment: (state: State, action: PayloadAction<Comment>) => {
            if (!state.order) {
                return;
            }

            const currentComments = [...(state.order.comments ?? [])];
            const updatedComment = action.payload;

            const updatedCommentIndex = currentComments.findIndex(
                c => c.id === updatedComment.id
            );

            if (updatedCommentIndex !== -1) {
                currentComments[updatedCommentIndex] = updatedComment;

                state.order = {
                    ...state.order,
                    comments: currentComments,
                };
            }
        },

        updateFulfillmentDetails: (
            state: State,
            action: PayloadAction<UpdateFulfillmentDetailsPayload>
        ) => {
            const { fulfillmentDetails, fulfillmentDistributionCenter } =
                action.payload;

            if (
                !state.order ||
                (!fulfillmentDetails && !fulfillmentDistributionCenter)
            ) {
                return;
            }

            const {
                fulfillmentTypeId,
                fulfillmentDistributionCenterId,
                distributionCenterId,
                fulfillmentToAddress = '',
                fulfillmentToCity = '',
                fulfillmentToRegion = '',
                fulfillmentToPostalCode = '',
                fulfillmentTo = '',
                residentialDelivery = false,
            } = fulfillmentDetails;

            state.order = {
                ...state.order,
                fulfillmentTypeId: fulfillmentTypeId as ORDER_FULFILLMENT_TYPE,
                fulfillmentDistributionCenterId:
                    fulfillmentDistributionCenterId ||
                    distributionCenterId ||
                    0,
                fulfillmentToAddress,
                fulfillmentToCity,
                fulfillmentToRegion,
                fulfillmentToPostalCode,
                fulfillmentTo,
                residentialDelivery,
                vendorDistributionCenter: fulfillmentDistributionCenter,
            };
        },

        updateFulfillmentLogistics: (state: State, action) => {
            if (!state.order) {
                return;
            }

            state.order = {
                ...state.order,
                fulfillmentLogistics: action.payload,
            };
        },

        addFolderAttachment: (
            state: State,
            action: PayloadAction<FolderAttachment>
        ) => {
            if (!state.order?.folder) {
                return;
            }

            const currentFolderAttachments = [
                ...(state.order.folder.folderAttachments ?? []),
            ];

            state.order = {
                ...state.order,
                folder: {
                    ...state.order.folder,
                    folderAttachments: [
                        ...currentFolderAttachments,
                        action.payload,
                    ],
                },
            };
        },

        removeFolderAttachment: (
            state: State,
            action: PayloadAction<number>
        ) => {
            if (!state.order?.folder) {
                return;
            }

            const filteredAttachments = (
                state.order.folder.folderAttachments ?? []
            ).filter(
                attachment =>
                    attachment.id.toString() !== action.payload.toString()
            );

            state.order = {
                ...state.order,
                folder: {
                    ...state.order.folder,
                    folderAttachments: filteredAttachments,
                },
            };
        },

        reset: () => {
            return initialState;
        },
    },
    selectors: {
        selectOrder: state => state.order,
    },
});

export const { selectOrder } = adminOrderSlice.selectors;

export const {
    initialize,
    initializeRoute,
    initializeFinancialInformation,
    addFolderAttachment,
    removeFolderAttachment,
    addNote,
    addComment,
    updateComment,
    addContact,
    removeContact,
    setOrder,
    updateOrder,
    updateKeyValuePair,
    setFinanceEdit,
    updateFulfillmentDetails,
    updateSpecialInstructions,
    updateFulfillmentLogistics,
    reset,
    resetInitialization,
} = adminOrderSlice.actions;

export default adminOrderSlice.reducer;
